import React, { useState, useEffect } from 'react';

import TemplatesWizardContext from './TemplatesWizardContext';
import SelectStart from './components/Steps/SelectStart/SelectStart';
import SelectLanguage from './components/Steps/SelectLanguage/SelectLanguage';
import SelectTemplateType from './components/Steps/SelectTemplateType/SelectTemplateType';
import SelectTestsNumber from './components/Steps/SelectTestsNumber/SelectTestsNumber';
import DefinePoints from './components/Steps/DefinePoints/DefinePoints';
import SetScale from './components/Steps/SetScale/SetScale';
import SetSuccessCriteria from './components/Steps/SetSuccessCriteria/SetSuccessCriteria';
import LogicMap from './components/Steps/LogicMap/LogicMap';
import SetContext from './components/Steps/SetContext/SetContext';
import UploadFile from './components/Steps/UploadFile/UploadFile';
import AIAnalysingFile from './components/Steps/AIAnalysingFile/AIAnalysingFile';

import {
  MC_QUESTION_OPTIONS,
  TEMPLATES_WIZARD_STEP_KEYS,
  SURVEY_LANGUAGES,
  TEMPLATES_WIZARD_START_OPTIONS,
  TEMPLATES_WIZARD_TEMPLATE_TYPES,
  TEMPLATES_SCALES
} from '../../helpers/constants';

import enStrings from './strings/en';
import nlStrings from './strings/nl';
import frStrings from './strings/fr';

import styleText from './helpers/styleText';

import closeIcon from '../../../../../../assets/img/delete.svg';

import styles from './TemplatesWizard.module.css';

export default ({
  setWizardHidden,
  actions,
  surveyId,
  surveyLanguage,
  isAdmin
}) => {
  const [currentStepKey, setCurrentStepKey] = useState(
    TEMPLATES_WIZARD_STEP_KEYS.SELECT_START
  );
  const [strings, setStrings] = useState(enStrings);
  const [steps, setSteps] = useState([]);

  const getWizardLanguageFromSurveyConfiguration = () => {
    if (surveyLanguage === SURVEY_LANGUAGES.DUTCH)
      return SURVEY_LANGUAGES.DUTCH;
    if (surveyLanguage === SURVEY_LANGUAGES.FRENCH)
      return SURVEY_LANGUAGES.FRENCH;

    // For English and German
    return SURVEY_LANGUAGES.ENGLISH;
  };
  const selectStartStep = [
    {
      key: TEMPLATES_WIZARD_STEP_KEYS.SELECT_START,
      component: <SelectStart />,
      value: TEMPLATES_WIZARD_START_OPTIONS.SELECT_TEMPLATE
    }
  ];

  const templateSteps = [
    {
      key: TEMPLATES_WIZARD_STEP_KEYS.SELECT_LANGUAGE,
      component: <SelectLanguage />,
      value: getWizardLanguageFromSurveyConfiguration()
    },
    {
      key: TEMPLATES_WIZARD_STEP_KEYS.SELECT_TEMPLATE_TYPE,
      component: <SelectTemplateType />,
      value: TEMPLATES_WIZARD_TEMPLATE_TYPES.PROBLEM_VALIDATION
    },
    {
      key: TEMPLATES_WIZARD_STEP_KEYS.SET_CONTEXT,
      component: <SetContext />,
      value: null
    },
    {
      key: TEMPLATES_WIZARD_STEP_KEYS.SELECT_TESTS_NUMBER,
      component: <SelectTestsNumber />,
      value: 3
    },
    {
      key: TEMPLATES_WIZARD_STEP_KEYS.DEFINE_POINTS,
      component: <DefinePoints />,
      value: []
    },
    {
      key: TEMPLATES_WIZARD_STEP_KEYS.SET_SCALE,
      component: <SetScale />,
      value: {
        type: TEMPLATES_SCALES.MULTIPLE_CHOICE,
        values: MC_QUESTION_OPTIONS[0]
      }
    },
    {
      key: TEMPLATES_WIZARD_STEP_KEYS.SET_SUCCESS_CRITERIA,
      component: <SetSuccessCriteria />,
      value: []
    },
    {
      key: TEMPLATES_WIZARD_STEP_KEYS.LOGIC_MAP,
      component: <LogicMap />,
      value: null
    }
  ];

  const uploadFileSteps = [
    {
      key: TEMPLATES_WIZARD_STEP_KEYS.UPLOAD_FILE,
      component: <UploadFile />,
      value: null
    },
    {
      key: TEMPLATES_WIZARD_STEP_KEYS.AI_ANALYSIS_FILE,
      component: <AIAnalysingFile />,
      value: null
    }
  ];

  useEffect(() => {
    setSteps([...selectStartStep, ...templateSteps]);
  }, []);

  const currentStep = steps.find(s => s.key === currentStepKey);

  const setStepValue = (stepKey, value) => {
    if (stepKey === TEMPLATES_WIZARD_STEP_KEYS.SELECT_START) {
      if (value === TEMPLATES_WIZARD_START_OPTIONS.SELECT_TEMPLATE) {
        setSteps([
          {
            ...selectStartStep[0],
            value: TEMPLATES_WIZARD_START_OPTIONS.SELECT_TEMPLATE
          },
          ...templateSteps
        ]);
      }
      if (value === TEMPLATES_WIZARD_START_OPTIONS.UPLOAD_FILE) {
        setSteps([
          {
            ...selectStartStep[0],
            value: TEMPLATES_WIZARD_START_OPTIONS.UPLOAD_FILE
          },
          ...uploadFileSteps
        ]);
      }
      if (value === TEMPLATES_WIZARD_START_OPTIONS.START_SCRATCH) {
        setSteps([
          {
            ...selectStartStep[0],
            value: TEMPLATES_WIZARD_START_OPTIONS.START_SCRATCH
          },
          ...uploadFileSteps
        ]);
      }
    } else {
      const stepsToEdit = steps;

      const setSingleStep = (sK, v) => {
        const stepToEdit = stepsToEdit.find(s => s.key === sK);
        stepToEdit.value = v;
      };

      if (Array.isArray(stepKey)) {
        stepKey.forEach((sK, sKi) => setSingleStep(sK, value[sKi]));
      } else {
        setSingleStep(stepKey, value);
      }

      setSteps([...stepsToEdit]);
    }
  };

  const goToNextStep = () => {
    const currentStepKeyIndex = steps.findIndex(s => s.key === currentStepKey);

    const startStepSelection = steps.find(
      s => s.key === TEMPLATES_WIZARD_STEP_KEYS.SELECT_START
    );

    let nextStepIndexIncrease = 1;
    if (
      startStepSelection &&
      startStepSelection.value ===
        TEMPLATES_WIZARD_START_OPTIONS.SELECT_TEMPLATE &&
      currentStepKeyIndex === 2
    ) {
      // For Problem Validation template type SET_CONTEXT step is skipped
      const templateTypeStep = steps.find(
        s => s.key === TEMPLATES_WIZARD_STEP_KEYS.SELECT_TEMPLATE_TYPE
      );

      if (
        templateTypeStep.value ===
        TEMPLATES_WIZARD_TEMPLATE_TYPES.PROBLEM_VALIDATION
      ) {
        nextStepIndexIncrease = 2;
      }
    }

    const nextStepKey = steps[currentStepKeyIndex + nextStepIndexIncrease].key;
    setCurrentStepKey(nextStepKey);
  };

  const goToPreviousStep = () => {
    const currentStepKeyIndex = steps.findIndex(s => s.key === currentStepKey);

    const startStepSelection = steps.find(
      s => s.key === TEMPLATES_WIZARD_STEP_KEYS.SELECT_START
    );

    let nextStepIndexDecrease = 1;
    if (
      startStepSelection &&
      startStepSelection.value ===
        TEMPLATES_WIZARD_START_OPTIONS.SELECT_TEMPLATE &&
      currentStepKeyIndex === 4
    ) {
      // For Problem Validation template type SET_CONTEXT step is skipped
      const templateTypeStep = steps.find(
        s => s.key === TEMPLATES_WIZARD_STEP_KEYS.SELECT_TEMPLATE_TYPE
      );

      if (
        templateTypeStep.value ===
        TEMPLATES_WIZARD_TEMPLATE_TYPES.PROBLEM_VALIDATION
      ) {
        nextStepIndexDecrease = 2;
      }
    }

    const previousStepKey =
      steps[currentStepKeyIndex - nextStepIndexDecrease].key;
    setCurrentStepKey(previousStepKey);
  };

  const resetWizartInputData = () => {
    setStepValue(
      [
        TEMPLATES_WIZARD_STEP_KEYS.DEFINE_POINTS,
        TEMPLATES_WIZARD_STEP_KEYS.SET_SUCCESS_CRITERIA
      ],
      [[], []]
    );
  };

  useEffect(
    () => {
      if (
        surveyLanguage === SURVEY_LANGUAGES.ENGLISH ||
        surveyLanguage === SURVEY_LANGUAGES.GERMAN
      ) {
        setStrings(enStrings);
      }
      if (surveyLanguage === SURVEY_LANGUAGES.DUTCH) {
        setStrings(nlStrings);
      }
      if (surveyLanguage === SURVEY_LANGUAGES.FRENCH) {
        setStrings(frStrings);
      }
    },
    [surveyLanguage]
  );

  if (!currentStep) return null;

  return (
    <TemplatesWizardContext.Provider
      value={{
        steps,
        setSteps,
        currentStepKey,
        setCurrentStepKey,
        currentStep,
        setStepValue,
        goToNextStep,
        goToPreviousStep,
        setWizardHidden,
        strings,
        styleText,
        actions,
        resetWizartInputData,
        surveyId,
        isAdmin
      }}
    >
      <img
        className={styles.closePopupButton}
        src={closeIcon}
        alt="Close icon"
        onClick={() => setWizardHidden(true)}
        role="presentation"
      />
      {currentStep.component}
    </TemplatesWizardContext.Provider>
  );
};
